import { createContext, use, type ReactNode } from 'react';

export interface BoardHostState {
  orgRef: string | null;
  isReady: boolean;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /** Effective operation permissions off the host's one standing read. Never an org role. */
  permissions: readonly string[] | null;
  /**
   * The signed-in account's BACKEND user id (never a Firebase uid), or null while the host has not
   * read it. The calendar asks it whose connected calendar is the viewer's own (CBG-CAL1): the
   * schedule dialog's "your calendar", and the meeting page's calendar file rule (question 10).
   */
  userId?: string | null;
  /** The signed-in account's own address, or null while unread: the board-account example's domain. */
  userEmail?: string | null;
  /**
   * The organization's name, or null while unread. The calendar's words name it where the backend
   * does: the Givmo-sent sender ("<Organization> Board") and the neutral invitation title.
   */
  orgName?: string | null;
  /** The organization's IANA time zone off the standing read, or null while unread or unset. */
  orgTimeZone?: string | null;
  /**
   * Where the host shows the AI tools connected to this organization (its console's Team & Access
   * page, as the CRM import's "How to connect" links it), or absent where it has none. The upload
   * page's "See what's connected" (cut inbox's review fold, M2).
   */
  connectedAi?: { href: string };
}

const HostContext = createContext<BoardHostState | null>(null);

export function BoardOfficerProvider({
  host,
  children,
}: {
  host: BoardHostState;
  children: ReactNode;
}) {
  return <HostContext value={host}>{children}</HostContext>;
}

export function useBoardHost(): BoardHostState {
  const host = use(HostContext);
  if (host === null) throw new Error('The Board officer surface needs its host organization.');
  return host;
}

export function hasBoardPermission(
  permissions: readonly string[] | null,
  permission: string,
): boolean {
  return permissions?.includes(permission) === true;
}

/**
 * Whether the person's settled permission set holds `permission` — the ONE string the operation's
 * backend decorator names. Every officer control gates on this, never on an organization role, and
 * a control the caller does not hold is ABSENT — never disabled with a note (Jonathan, 2026-09-28).
 */
export function useBoardPermission(permission: string): boolean {
  const { permissions } = useBoardHost();
  return hasBoardPermission(permissions, permission);
}
